import { useCallback, useEffect, useState, useMemo } from 'react';
import { message } from 'antd';
import PropTypes from 'prop-types';
import { useUpload } from 'hooks/useUpload';
import { useSelector, useDispatch } from 'react-redux';
import { UPLOAD_DOCUMENT_TRANSLATIONS } from 'const/translations';
import { PROP_TYPE_STATUS } from 'const/propTypes';
import { STATUS_ERROR, STATUS_SUCCESS } from 'const';
import { UploadManagerDesktopLayout } from 'components/UploadDocuments/FileUploadManager/UploadManagerDesktopLayout';
import { UploadManagerMobileLayout } from 'components/UploadDocuments/FileUploadManager/UploadManagerMobileLayout';
import { isFileForbidden } from 'helpers/common';
import { openSuccessNotification } from 'helpers/notifications';
import {
  selectUserCurrentCompanyPreCompta,
  selectUserCurrentCompanyPreComptaMailCollector,
  selectUserCurrentCompanyPreComptaMail,
} from 'modules/loggedUserInfo/selectors';
import {
  uploadFilesRequest,
  uploadFilesResetStatus,
} from 'modules/uploadDocuments/actions';
import {
  selectUploadPayload,
  selectUploadErrors,
} from 'helpers/UploadDocuments';

const {
  MESSAGES: { ERROR, SUCCESS },
} = UPLOAD_DOCUMENT_TRANSLATIONS;

export const typeSelectOptions = [
  { label: 'Achat', value: 'achat' },
  { label: 'Vente', value: 'vente' },
  { label: 'Autre', value: 'autre' },
];

export const FileUploadManager = ({ uploadStatus, isMobileVersion }) => {
  const dispatch = useDispatch();
  const isPreCompta = useSelector(selectUserCurrentCompanyPreCompta);
  const isPreComptaMailCollector = useSelector(
    selectUserCurrentCompanyPreComptaMailCollector
  );
  const preComptaMail = useSelector(selectUserCurrentCompanyPreComptaMail);

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [type, setType] = useState(typeSelectOptions[0].value);
  const [submitted, setSubmitted] = useState(false);
  const [isOneFileEditing, setIsOneFileEditing] = useState(false);

  useEffect(() => {
    if ([STATUS_SUCCESS, STATUS_ERROR].includes(uploadStatus) && submitted) {
      setUploadedFiles([]);
      setSubmitted(false);
      if (uploadStatus === STATUS_SUCCESS) {
        openSuccessNotification({ message: SUCCESS });
      }
      dispatch(uploadFilesResetStatus());
    }
  }, [dispatch, submitted, uploadStatus]);

  const onUploadError = useCallback((errorMessage) => {
    message.error(errorMessage);
  }, []);

  const uploadToServer = useCallback(() => {
    dispatch(
      uploadFilesRequest({
        uploadedFiles: selectUploadPayload(uploadedFiles, isPreCompta),
        onError: onUploadError,
        type,
      })
    );
  }, [dispatch, uploadedFiles, isPreCompta, onUploadError, type]);

  useEffect(() => {
    if (submitted) {
      uploadToServer();
    }
  }, [submitted, uploadToServer]);

  const { handleChange, handleRemove, handleReset } = useUpload(
    uploadedFiles,
    setUploadedFiles,
    true
  );

  const initiateUpload = useCallback(() => setSubmitted(true), []);

  const beforeUpload = useCallback((file) => {
    if (isFileForbidden(file)) {
      message.error(ERROR.replace('{{FILE_NAME}}', file.name));
      return Promise.reject();
    }
    return false; // do not send to the backend
  }, []);

  const listHasValidFiles = useMemo(
    () =>
      !!selectUploadPayload(uploadedFiles, isPreCompta).length &&
      !selectUploadErrors(uploadedFiles).length &&
      !isOneFileEditing,
    [isOneFileEditing, isPreCompta, uploadedFiles]
  );

  const UploadManagerLayout = isMobileVersion
    ? UploadManagerMobileLayout
    : UploadManagerDesktopLayout;

  return (
    <UploadManagerLayout
      handleChange={handleChange}
      uploadedFiles={uploadedFiles}
      beforeUpload={beforeUpload}
      handleRemove={handleRemove}
      handleReset={handleReset}
      initiateUpload={initiateUpload}
      submitted={submitted}
      listHasValidFiles={listHasValidFiles}
      isPreCompta={isPreCompta}
      isPreComptaMailCollector={isPreComptaMailCollector}
      preComptaMail={preComptaMail}
      isRenameable
      setIsOneFileEditing={setIsOneFileEditing}
      setType={setType}
      type={type}
    />
  );
};

FileUploadManager.propTypes = {
  uploadStatus: PROP_TYPE_STATUS,
  isMobileVersion: PropTypes.bool,
};
